import React, { useState } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
  || 'https://ai-image-to-cal-production.up.railway.app';

function UploadForm({ onSuccess, onError, setLoading, loading }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      onError('');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file?.type.startsWith('image/')) {
      setSelectedFile(file);
      onError('');
    } else if (file) {
      onError('Choose an image file to continue.');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      onError('Please select a file first.');
      return;
    }

    setLoading(true);
    onError('');

    const formData = new FormData();
    formData.append('screenshot', selectedFile);

    try {
      const response = await fetch(`${API_BASE_URL}/api/upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'We could not process that screenshot. Please try again.');
      }

      const data = await response.json();
      onSuccess(data.icsURL, data.googleCalendarURL);
    } catch (err) {
      onError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="upload-form" onSubmit={handleSubmit}>
      <label
        className={`drop-zone${isDragging ? ' is-dragging' : ''}${selectedFile ? ' has-file' : ''}`}
        onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <input
          className="file-input"
          type="file"
          accept="image/png,image/jpeg,image/webp"
          onChange={handleFileChange}
          disabled={loading}
        />

        {selectedFile ? (
          <>
            <span className="file-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M14 3H6.8A2.8 2.8 0 0 0 4 5.8v12.4A2.8 2.8 0 0 0 6.8 21h10.4a2.8 2.8 0 0 0 2.8-2.8V9l-6-6Z" />
                <path d="M14 3v6h6M8 16l2.5-2.5 2 2 1.5-1.5 2 2" />
              </svg>
            </span>
            <span className="selected-file-copy">
              <strong>{selectedFile.name}</strong>
              <span>{formatFileSize(selectedFile.size)} · Click to replace</span>
            </span>
            <span className="file-check" aria-hidden="true">✓</span>
          </>
        ) : (
          <>
            <span className="upload-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M12 16V4M7.5 8.5 12 4l4.5 4.5M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" />
              </svg>
            </span>
            <span className="drop-copy"><strong>Choose image</strong> or drag here</span>
          </>
        )}
      </label>

      <button className="primary-button" type="submit" disabled={loading || !selectedFile}>
        {loading ? (
          <><span className="spinner" aria-hidden="true" />Reading your schedule…</>
        ) : (
          <>Convert <span aria-hidden="true">→</span></>
        )}
      </button>
    </form>
  );
}

export default UploadForm;
